import {
  createMutationId,
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import {escapeOptimisticTitleHTML} from '../utils/helpers'
import type {
  UpdateIssueInput,
  updateIssueTitleMutation,
  updateIssueTitleMutation$data,
} from './__generated__/updateIssueTitleMutation.graphql'

/** Packages a mutation response into the `{id, values}` shape the event carries, or
 * `undefined` if the affected node is missing (e.g. an unexpectedly null payload field). */
function toObjectPropertiesPatch(data: updateIssueTitleMutation$data): ObjectPropertiesPatch | undefined {
  const issue = data.updateIssue?.issue
  if (!issue) return undefined
  return {id: issue.id, values: {title: issue.title, titleHTML: issue.titleHTML}}
}

export function commitUpdateIssueTitleMutation({
  environment,
  input: {issueId, title},
  onError,
  onCompleted,
}: {
  environment: Environment
  input: {issueId: string; title: string | null}
  onError?: (error: Error) => void
  onCompleted?: (response: updateIssueTitleMutation$data) => void
}) {
  const inputHash: UpdateIssueInput = {
    id: issueId,
  }
  if (title !== null) {
    inputHash.title = title
  }

  const optimisticResponse: updateIssueTitleMutation$data = {
    updateIssue: {
      issue: {
        id: issueId,
        title: title || '',
        titleHTML: escapeOptimisticTitleHTML(title || ''),
      },
    },
  }

  // Constructing the optimistic event up front (rather than dispatching it immediately)
  // lets its `mutationId` be generated by the `MutationLifecycleEvent` constructor, then
  // reused below for the completed/error events so all three correlate to this same
  // commitUpdateIssueTitleMutation call. `createMutationId()` is only a fallback for the
  // (practically unreachable, since `issueId` is always set) case where there's no node
  // to build an optimistic patch from.
  const optimisticPatch = toObjectPropertiesPatch(optimisticResponse)
  const optimisticEvent =
    optimisticPatch &&
    new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'optimistic', data: optimisticPatch})
  const mutationId = optimisticEvent?.detail.mutationId ?? createMutationId()

  const disposable = commitMutation<updateIssueTitleMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation updateIssueTitleMutation($input: UpdateIssueInput!) @raw_response_type {
        updateIssue(input: $input) {
          issue {
            id
            title
            titleHTML
          }
        }
      }
    `,
    variables: {
      input: inputHash,
    },
    onError: error => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
      )
      if (onError) onError(error)
    },
    optimisticResponse,
    onCompleted: response => {
      const patch = toObjectPropertiesPatch(response)
      if (patch) {
        dispatchMutationLifecycleEvent(
          new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
            phase: 'completed',
            mutationId,
            data: patch,
          }),
        )
      }
      if (onCompleted) onCompleted(response)
    },
  })

  // Relay applies `optimisticResponse` synchronously as part of `commitMutation`,
  // so it's safe to dispatch the optimistic phase event right after committing.
  if (optimisticEvent) {
    dispatchMutationLifecycleEvent(optimisticEvent)
  }

  return disposable
}
